import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const SHAPE_OPTIONS = [
  { value: "rect", label: "Rect" },
  { value: "circle", label: "Circle" },
  { value: "ellipse", label: "Ellipse" },
  { value: "diamond", label: "Diamond" },
  { value: "triangle", label: "Triangle" },
];

const SHAPE_PRESETS = [
  { type: "rect", label: "Add Rect" },
  { type: "circle", label: "Add Circle" },
  { type: "diamond", label: "Add Diamond" },
  { type: "triangle", label: "Add Triangle" },
];

const BORDER_TYPE_OPTIONS = [
  { value: "solid", label: "Solid" },
  { value: "dashed", label: "Dashed" },
  { value: "dotted", label: "Dotted" },
];

const PROPERTY_FIELDS = [
  { key: "x", label: "X", inputMode: "decimal" },
  { key: "y", label: "Y", inputMode: "decimal" },
  { key: "z", label: "Z", inputMode: "decimal" },
  { key: "width", label: "W", inputMode: "decimal" },
  { key: "height", label: "H", inputMode: "decimal" },
  { key: "originX", label: "OX", inputMode: "decimal" },
  { key: "originY", label: "OY", inputMode: "decimal" },
  { key: "rotate", label: "Rot", inputMode: "decimal" },
  { key: "opacity", label: "Opacity", inputMode: "decimal" },
  { key: "fontSize", label: "Font", inputMode: "decimal" },
  { key: "radiusTopLeft", label: "RTL", inputMode: "decimal" },
  { key: "radiusTopRight", label: "RTR", inputMode: "decimal" },
  { key: "radiusBottomLeft", label: "RBL", inputMode: "decimal" },
  { key: "radiusBottomRight", label: "RBR", inputMode: "decimal" },
];

const ShapeEditorTools = ({
  activeTab = "",
  shapes = [],
  selectedShapeId = null,
  selectedShapeIds = [],
  onSelectShape,
  onAddShape,
  onCloneShape,
  onCopyShapes,
  onPasteShapes,
  onDeleteShape,
  onResetShape,
  onShapeFieldChange,
  buildSelectedShapeDsl,
  buildAllShapesDsl,
  onInsertScriptSnippet,
}) => {
  const [copyState, setCopyState] = useState({
    scope: "",
    type: "",
    message: "",
  });
  const copyTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      window.clearTimeout(copyTimerRef.current);
    };
  }, []);

  const selectedShape = useMemo(
    () => shapes.find((shape) => shape.shapeId === selectedShapeId) ?? null,
    [shapes, selectedShapeId],
  );
  const selectedCount = selectedShapeIds.length;
  const isSelectedShapeVisible =
    String(selectedShape?.visibility ?? "true") !== "false";

  const handleCopy = useCallback(async (scope, text, successMessage) => {
    const payload = String(text || "").trim();
    if (!payload) return;

    try {
      if (!navigator.clipboard?.writeText) {
        throw new Error("Clipboard unavailable");
      }
      await navigator.clipboard.writeText(payload);
      setCopyState({
        scope,
        type: "success",
        message: successMessage,
      });
    } catch {
      setCopyState({
        scope,
        type: "error",
        message: "Copy failed",
      });
    }

    window.clearTimeout(copyTimerRef.current);
    copyTimerRef.current = window.setTimeout(() => {
      setCopyState({ scope: "", type: "", message: "" });
    }, 1800);
  }, []);

  const handleInsert = useCallback(
    (scope, text, successMessage) => {
      const payload = String(text || "");
      if (!payload.trim()) return;
      const didInsert = onInsertScriptSnippet?.(payload);
      setCopyState({
        scope,
        type: didInsert ? "success" : "error",
        message: didInsert ? successMessage : "Insert failed",
      });
      window.clearTimeout(copyTimerRef.current);
      copyTimerRef.current = window.setTimeout(() => {
        setCopyState({ scope: "", type: "", message: "" });
      }, 1800);
    },
    [onInsertScriptSnippet],
  );

  if (activeTab !== "shapes") return null;

  return (
    <div className="editor-tools-bar editor-shape-tools">
      <div className="editor-tools-group editor-shape-tools-group">
        <div className="editor-tools-group-header">
          <div className="editor-tools-group-heading">
            <span className="editor-tools-group-title">Shapes</span>
            <span className="editor-tools-hint">
              {selectedCount > 1
                ? `${selectedCount} selected`
                : "Select, drag, resize, then copy DSL"}
            </span>
          </div>
          <div className="editor-tools-group-actions">
            <button
              type="button"
              className="editor-tools-btn"
              onClick={() =>
                handleCopy(
                  "all",
                  buildAllShapesDsl?.() ?? "",
                  "Shape DSL copied",
                )
              }
              disabled={!shapes.length}
            >
              Copy all
            </button>
            <button
              type="button"
              className="editor-tools-btn"
              onClick={() =>
                handleInsert(
                  "all",
                  buildAllShapesDsl?.() ?? "",
                  "Inserted all shapes",
                )
              }
              disabled={!shapes.length}
            >
              Insert all
            </button>
            <button
              type="button"
              className="editor-tools-btn"
              onClick={onCopyShapes}
              disabled={!selectedCount}
            >
              Copy shapes
            </button>
            <button
              type="button"
              className="editor-tools-btn"
              onClick={onPasteShapes}
            >
              Paste
            </button>
          </div>
        </div>

        <div className="editor-shape-preset-row">
          {SHAPE_PRESETS.map((preset) => (
            <button
              key={preset.type}
              type="button"
              className="editor-tools-btn"
              onClick={() => onAddShape?.(preset.type)}
            >
              {preset.label}
            </button>
          ))}
        </div>

        <div className="editor-shape-layout">
          <div className="editor-shape-list" role="list" aria-label="Shapes">
            {shapes.map((shape) => {
              const isActive = selectedShapeIds.includes(shape.shapeId);
              return (
                <button
                  key={shape.shapeId}
                  type="button"
                  className={`editor-shape-list-item${isActive ? " active" : ""}`}
                  onClick={(event) =>
                    onSelectShape?.(shape.shapeId, {
                      toggle: Boolean(event.shiftKey),
                    })
                  }
                >
                  <span className="editor-shape-list-item-name">
                    {shape.name || shape.shapeId}
                  </span>
                  <span className="editor-shape-list-item-meta">
                    {shape.shape}
                    {shape.isNew ? " · new" : shape.isDraft ? " · edited" : ""}
                  </span>
                </button>
              );
            })}
            {!shapes.length ? (
              <div className="editor-shape-empty">
                Add a shape or select one from the preview.
              </div>
            ) : null}
          </div>

          <div className="editor-shape-detail">
            {selectedShape ? (
              <>
                <div className="editor-shape-form-grid">
                  <label className="editor-tools-coordinate-field editor-shape-span-2">
                    <span>Name</span>
                    <input
                      type="text"
                      className="editor-tools-coordinate-input editor-shape-wide-input"
                      value={selectedShape.name ?? ""}
                      onChange={(event) =>
                        onShapeFieldChange?.(
                          selectedShape.shapeId,
                          "name",
                          event.target.value,
                        )
                      }
                    />
                  </label>
                  <label className="editor-tools-coordinate-field">
                    <span>Shape</span>
                    <select
                      className="editor-tools-coordinate-input"
                      value={selectedShape.shape ?? "rect"}
                      onChange={(event) =>
                        onShapeFieldChange?.(
                          selectedShape.shapeId,
                          "shape",
                          event.target.value,
                        )
                      }
                    >
                      {SHAPE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="editor-tools-coordinate-field">
                    <span>Visible</span>
                    <button
                      type="button"
                      className={`editor-tools-btn ${isSelectedShapeVisible ? "active" : ""}`}
                      onClick={() =>
                        onShapeFieldChange?.(
                          selectedShape.shapeId,
                          "visibility",
                          String(!isSelectedShapeVisible),
                        )
                      }
                    >
                      {isSelectedShapeVisible ? "Shown" : "Hidden"}
                    </button>
                  </label>
                  {PROPERTY_FIELDS.map((field) => (
                    <label
                      key={field.key}
                      className="editor-tools-coordinate-field"
                    >
                      <span>{field.label}</span>
                      <input
                        type="text"
                        inputMode={field.inputMode}
                        className="editor-tools-coordinate-input"
                        value={selectedShape?.[field.key] ?? ""}
                        onChange={(event) =>
                          onShapeFieldChange?.(
                            selectedShape.shapeId,
                            field.key,
                            event.target.value,
                          )
                        }
                      />
                    </label>
                  ))}
                  <label className="editor-tools-coordinate-field">
                    <span>Fill</span>
                    <input
                      type="color"
                      className="editor-tools-color-input"
                      value={selectedShape.backgroundColor || "#22d3ee"}
                      onChange={(event) =>
                        onShapeFieldChange?.(
                          selectedShape.shapeId,
                          "backgroundColor",
                          event.target.value,
                        )
                      }
                    />
                  </label>
                  <label className="editor-tools-coordinate-field">
                    <span>Text</span>
                    <input
                      type="color"
                      className="editor-tools-color-input"
                      value={selectedShape.color || "#ffffff"}
                      onChange={(event) =>
                        onShapeFieldChange?.(
                          selectedShape.shapeId,
                          "color",
                          event.target.value,
                        )
                      }
                    />
                  </label>
                  <label className="editor-tools-coordinate-field">
                    <span>Border</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      className="editor-tools-coordinate-input"
                      value={selectedShape.borderWidth ?? ""}
                      onChange={(event) =>
                        onShapeFieldChange?.(
                          selectedShape.shapeId,
                          "borderWidth",
                          event.target.value,
                        )
                      }
                    />
                  </label>
                  <label className="editor-tools-coordinate-field">
                    <span>Bd Type</span>
                    <select
                      className="editor-tools-coordinate-input"
                      value={selectedShape.borderType ?? "solid"}
                      onChange={(event) =>
                        onShapeFieldChange?.(
                          selectedShape.shapeId,
                          "borderType",
                          event.target.value,
                        )
                      }
                    >
                      {BORDER_TYPE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="editor-tools-coordinate-field">
                    <span>Bd Color</span>
                    <input
                      type="color"
                      className="editor-tools-color-input"
                      value={selectedShape.borderColor || "#000000"}
                      onChange={(event) =>
                        onShapeFieldChange?.(
                          selectedShape.shapeId,
                          "borderColor",
                          event.target.value,
                        )
                      }
                    />
                  </label>
                  <label className="editor-tools-coordinate-field">
                    <span>Sh Blur</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      className="editor-tools-coordinate-input"
                      value={selectedShape.shadowBlur ?? ""}
                      onChange={(event) =>
                        onShapeFieldChange?.(
                          selectedShape.shapeId,
                          "shadowBlur",
                          event.target.value,
                        )
                      }
                    />
                  </label>
                  <label className="editor-tools-coordinate-field">
                    <span>Sh X</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      className="editor-tools-coordinate-input"
                      value={selectedShape.shadowOffsetX ?? ""}
                      onChange={(event) =>
                        onShapeFieldChange?.(
                          selectedShape.shapeId,
                          "shadowOffsetX",
                          event.target.value,
                        )
                      }
                    />
                  </label>
                  <label className="editor-tools-coordinate-field">
                    <span>Sh Y</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      className="editor-tools-coordinate-input"
                      value={selectedShape.shadowOffsetY ?? ""}
                      onChange={(event) =>
                        onShapeFieldChange?.(
                          selectedShape.shapeId,
                          "shadowOffsetY",
                          event.target.value,
                        )
                      }
                    />
                  </label>
                  <label className="editor-tools-coordinate-field">
                    <span>Sh Color</span>
                    <input
                      type="color"
                      className="editor-tools-color-input"
                      value={
                        String(selectedShape.shadowColor || "").slice(0, 7) ||
                        "#000000"
                      }
                      onChange={(event) =>
                        onShapeFieldChange?.(
                          selectedShape.shapeId,
                          "shadowColor",
                          event.target.value,
                        )
                      }
                    />
                  </label>
                  <label className="editor-tools-coordinate-field editor-shape-span-2">
                    <span>Text Value</span>
                    <textarea
                      className="editor-shape-textarea"
                      value={selectedShape.text ?? ""}
                      onChange={(event) =>
                        onShapeFieldChange?.(
                          selectedShape.shapeId,
                          "text",
                          event.target.value,
                        )
                      }
                    />
                  </label>
                </div>

                <div className="editor-shape-action-row">
                  <button
                    type="button"
                    className="editor-tools-btn"
                    onClick={() => onCloneShape?.(selectedShapeIds)}
                  >
                    {selectedCount > 1 ? "Clone selected" : "Clone"}
                  </button>
                  <button
                    type="button"
                    className="editor-tools-btn"
                    onClick={() =>
                      handleCopy(
                        "selected",
                        buildSelectedShapeDsl?.(selectedShapeIds) ?? "",
                        selectedCount > 1 ? "Selected DSL copied" : "Shape DSL copied",
                      )
                    }
                  >
                    {selectedCount > 1 ? "Copy selected DSL" : "Copy selected"}
                  </button>
                  <button
                    type="button"
                    className="editor-tools-btn"
                    onClick={() =>
                      handleInsert(
                        "selected",
                        buildSelectedShapeDsl?.(selectedShapeIds) ?? "",
                        selectedCount > 1
                          ? "Inserted selected shapes"
                          : "Inserted selected shape",
                      )
                    }
                  >
                    {selectedCount > 1 ? "Insert selected DSL" : "Insert selected"}
                  </button>
                  {selectedShape.isNew ? (
                    <button
                      type="button"
                      className="editor-tools-btn"
                      onClick={() => onDeleteShape?.(selectedShapeIds)}
                    >
                      {selectedCount > 1 ? "Delete selected" : "Delete"}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="editor-tools-btn"
                      onClick={() => onResetShape?.(selectedShapeIds)}
                    >
                      {selectedCount > 1 ? "Revert selected" : "Revert"}
                    </button>
                  )}
                </div>

                <p className="editor-tools-hint editor-shape-footer-hint">
                  Drag a selected shape on the preview to move it. Drag corner
                  handles to resize.
                </p>
                <div className="editor-shape-shortcuts">
                  <span className="editor-tools-hint">Shortcuts</span>
                  <span className="editor-tools-hint">Ctrl/Cmd+A Select all</span>
                  <span className="editor-tools-hint">Ctrl/Cmd+C Copy shapes</span>
                  <span className="editor-tools-hint">Ctrl/Cmd+V Paste at last click</span>
                  <span className="editor-tools-hint">Ctrl/Cmd+D Clone selected</span>
                  <span className="editor-tools-hint">Delete Remove selected</span>
                  <span className="editor-tools-hint">Esc Clear selection</span>
                  <span className="editor-tools-hint">H Toggle visibility</span>
                  <span className="editor-tools-hint">Shift+1/2/3/4 Add rect/circle/diamond/triangle</span>
                </div>
              </>
            ) : (
              <div className="editor-shape-empty">
                Select a shape from the preview or add a new one.
              </div>
            )}
          </div>
        </div>

        {copyState.message ? (
          <p
            className={`animation-workbench-copy-status editor-tools-copy-status ${copyState.type}`}
          >
            {copyState.message}
          </p>
        ) : null}
      </div>
    </div>
  );
};

export default ShapeEditorTools;
